import { toast } from "react-toastify";
import { userAccessEnum } from "../../../config/accessEnum";
import tools from "../../../helpers/tools";
import Can from "../../common/Can/Can";
import LicensePlate from "../../common/LicensePlate/LicensePlate";
import BrandLogo from "../Brand/BrandLogo";
import styles from "./QuotationLine.module.scss";

const STATUS_CLASSES = {
  1: styles.statusDraft,
  2: styles.statusValidated,
  3: styles.statusFinalized,
};

const FINANCE_STATUS_CLASSES = {
  1: styles.statusDraft, // Brouillon
  2: styles.statusValidated, // Demande envoyée
  3: styles.statusFinalized, // Accord reçu
  4: styles.financeStatusLocked, // Dossier complet
  5: styles.financeStatusTransmitted, // Dossier transmis
  6: styles.financeStatusSigned, // Transmis et signé
};

const QuotationHeader = ({
  quotation,
  index,

  state,
  onToggleDetails,
  deleteQuotation,
  duplicateQuotation,
  openPdf,
  setQuotationOpened,
  setQuotationStatus,
  match,
  overOverlay,
  clientUuid,
  onOpenFinanceModal,
  financeStatus,
}) => {
  const financeLink = (
    <Can I={userAccessEnum.MODIFIER_FINANCEMENT}>
      <>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={() => onOpenFinanceModal(quotation.uuid)}
          title={
            financeStatus
              ? "Voir la demande de financement liée à ce devis"
              : "Créer ou ouvrir la demande de financement liée à ce devis"
          }
        >
          <i className="fa fa-coins mr-1" />
          {financeStatus ? "Voir le financement" : "Financer"}
        </button>
        {financeStatus && (
          <span
            className={`${styles.statusPill} ${
              FINANCE_STATUS_CLASSES[financeStatus.statut] || ""
            }`}
            title={`Demande ${financeStatus.libelle}`}
          >
            {financeStatus.libelle}
          </span>
        )}
        <a
          className={`${styles.btn} ${styles.btnIcon}`}
          href={
            "/financements/create?devis=" +
            quotation.uuid +
            (clientUuid ? "&client=" + clientUuid : "")
          }
          target="_blank"
          rel="noopener noreferrer"
          title="Ouvrir dans un nouvel onglet"
        >
          <i className="fa fa-external-link-alt" />
        </a>
      </>
    </Can>
  );

  const generateContractsButton = (
    <button
      type="button"
      className={`${styles.btn} ${styles.btnBlue}`}
      data-toggle="modal"
      data-target="#genContractModal"
      onClick={setQuotationOpened}
    >
      Générer des contrats
      <span className={styles.btnCount}>
        {parseInt(quotation.quantity) - (quotation.contracts?.length || 0)}
      </span>
    </button>
  );

  return (
    <div className={styles.lineHead}>
      <div className={styles.lineHeadLeft}>
        <span
          className={`${styles.statusPill} ${
            STATUS_CLASSES[quotation.status.id] || ""
          }`}
        >
          {
            tools.findIn(
              state.constant.items.QUOTATION_STATUS,
              "id",
              quotation.status.id,
            ).name
          }
        </span>
        <BrandLogo brandName={quotation.vehicle.brand.name} />
        <button
          type="button"
          className={styles.lineRef}
          onClick={onToggleDetails}
        >
          {quotation.reference}
          <i className="fa fa-external-link-alt ml-2" />
        </button>
        <span className={styles.metaPill}>
          {quotation.dur} mois · {quotation.km} km
        </span>
        {(quotation.hitech_contracts || [])
          .filter((c) => c.immat)
          .map((c) => (
            <LicensePlate key={c.uuid} value={c.immat} size="sm" />
          ))}
        {/* LS-702 (recette) — état de l'envoi en signature, visible sans ouvrir la
            modale des CP. */}
        {quotation.signatureStatus && (
          <span
            className={`${styles.statusPill} ${
              quotation.signatureStatus === "signee"
                ? styles.financeStatusSigned
                : styles.financeStatusTransmitted
            }`}
            title={
              (quotation.signatureStatusLabel || "Envoyée en signature") +
              (quotation.signatureMode === "manuel" ? " (envoi manuel)" : "") +
              (quotation.signatureEnvoyeeLe
                ? " le " +
                  new Date(quotation.signatureEnvoyeeLe).toLocaleDateString(
                    "fr-FR",
                  )
                : "")
            }
          >
            <i className="fa fa-file-signature mr-1" />
            {quotation.signatureStatusLabel ||
              (quotation.signatureStatus === "signee"
                ? "Proposition signée"
                : "Proposition envoyée")}
            {quotation.signatureMode === "manuel" && " (manuel)"}
          </span>
        )}
        <span
          className={styles.metaPill}
          title={quotation.vehicle.optionsNotIncluded
            .map(
              (qopt) =>
                qopt.name +
                " - " +
                qopt.priceHT +
                "€ " +
                (qopt.isInclVR ? " (*VR)" : ""),
            )
            .join("\n")}
        >
          {quotation.vehicle.optionsNotIncluded.length}{" "}
          {tools.getStringPlural(
            "option",
            quotation.vehicle.optionsNotIncluded.length > 1,
          )}
        </span>
        {quotation.comment && quotation.comment != "" && (
          <span
            className={`${styles.metaPill} ${styles.metaPillClick}`}
            onClick={(e) => {
              e.stopPropagation();
              toast.info(quotation.comment, {
                autoClose: 15000,
              });
            }}
          >
            <i className="fa fa-comment mr-1" title={quotation.comment} />
            commentaire
          </span>
        )}
      </div>
      <div className={styles.lineHeadActions} style={overOverlay}>
        {match.params.uuid != "create" && quotation.uuid ? (
          <>
            <button
              type="button"
              title="Dupliquer ce devis"
              className={`${styles.btn} ${styles.btnIcon}`}
              onClick={() => duplicateQuotation(index)}
            >
              <i className="fa fa-copy" />
            </button>
            {quotation.status.id != 2 && quotation.status.id != 3 ? (
              <>
                <button type="button" className={styles.btn} onClick={openPdf}>
                  Voir les CP
                </button>
                <Can I={userAccessEnum.MODIFIER_STATUT_LOI_DE_ROULAGE}>
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnPrimary}`}
                    onClick={() => setQuotationStatus(index, 2)}
                  >
                    Valider ce devis
                  </button>
                </Can>
                <button
                  type="button"
                  title="Supprimer cette ligne"
                  className={`${styles.btn} ${styles.btnIcon} ${styles.btnDanger}`}
                  onClick={deleteQuotation}
                >
                  <i className="fa fa-times" />
                </button>
              </>
            ) : quotation.status.id == 2 ? (
              <>
                <Can I={userAccessEnum.MODIFIER_STATUT_LOI_DE_ROULAGE}>
                  <button
                    type="button"
                    className={styles.btn}
                    onClick={() => setQuotationStatus(index, 1)}
                  >
                    Repasser en brouillon
                  </button>
                </Can>
                <button type="button" className={styles.btn} onClick={openPdf}>
                  Voir les CP
                </button>
                <Can I={userAccessEnum.MODIFIER_STATUT_LOI_DE_ROULAGE}>
                  {generateContractsButton}
                </Can>
                {financeLink}
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnBlue}`}
                  data-toggle="modal"
                  data-target="#viewContractModal"
                  onClick={setQuotationOpened}
                >
                  {quotation.contracts?.length || 0} contrats
                </button>
                {(quotation.contracts?.length || 0) < quotation.quantity &&
                  generateContractsButton}
                <button type="button" className={styles.btn} onClick={openPdf}>
                  Voir les CP
                </button>
                {financeLink}
              </>
            )}
          </>
        ) : (
          <>
            <span
              className={styles.unsavedPill}
              title="Cliquez sur créer ou enregistrer le devis pour sauvegarder cette nouvelle ligne"
              onClick={() =>
                alert(
                  "Cliquez sur créer ou enregistrer le devis pour sauvegarder cette nouvelle ligne",
                )
              }
            >
              <i className="fa fa-exclamation-triangle mr-1" />
              Ligne non enregistrée
            </span>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnIcon} ${styles.btnDanger}`}
              onClick={deleteQuotation}
            >
              <i className="fa fa-times" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default QuotationHeader;
